import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, Camera, User, LayoutList, BarChart2, Pencil, MapPin } from 'lucide-react';
import * as api from '../services/api';
import AttendanceTimeline, { type TimelineRecord } from '../components/AttendanceTimeline';
import MasterLinkButton from '../components/MasterLinkButton';
import { useSort, usePager, SortableTh } from '../components/TableTools';
import { SmartDateInput, SmartTimeInput } from '../components/SmartDateTime';
import type ExcelJS from 'exceljs';
import { downloadWorkbook, styleHeader, styleDataRow, styleTitle, styleNoteRow } from '../utils/excelHelper';
import { hasFeature } from '../utils/featureFlags';
import { useAlcoholThresholds, judgeAlcohol } from '../utils/alcoholThresholds';

// ---- 統合表示（unified_work_types）の呼称と体裁 ----
// フラグONの会社では「特別作業」という区分を画面に出さず、通常業務と同じ「業務」として見せる。
// フラグOFF（従来）の会社は運用中のため、呼称・色・列構成をいっさい変えない。
const isUnified = (): boolean => hasFeature('unified_work_types');

/** 臨時業務まわりのアクセント色。統合表示では通常業務と同じ青系にする */
function actAccent(): { bg: string; fg: string; border: string } {
  return isUnified()
    ? { bg: '#eff6ff', fg: '#1d4ed8', border: '#bfdbfe' }
    : { bg: '#f5f3ff', fg: '#7c3aed', border: '#c4b5fd' };
}

/** 臨時業務の登録ボタン等の文言 */
const actLabel = {
  /** 「＋ 作業を追加」/「＋ 特別作業」 */
  add:      () => (isUnified() ? '作業を追加' : '特別作業'),
  /** 行の操作ボタン */
  rowBtn:   () => (isUnified() ? '作業' : '特別作業'),
  /** モーダル見出し */
  modal:    () => (isUnified() ? '作業の記録' : '特別作業'),
  /** 一覧見出し */
  list:     () => (isUnified() ? '登録済みの作業' : '登録済み特別作業'),
};

/** 業務名バッジの色。統合表示では通常業務と臨時業務を同じ体裁にする */
function WORK_BADGE_STYLE(): { background: string; color: string; border: string } {
  return isUnified()
    ? { background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }
    : { background: '#F3E8FF', color: '#7C3AED', border: 'none' };
}

function FaceImage({ apiBase, path, onOpen }: {
  apiBase: string;
  path: string;
  onOpen: (blobUrl: string) => void;
}) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    const token = api.getToken() ?? '';
    let objectUrl: string | null = null;
    fetch(`${apiBase}/faces/${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.blob() : Promise.reject())
      .then(b => {
        objectUrl = URL.createObjectURL(b);
        setSrc(objectUrl);
      })
      .catch(() => {});
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [apiBase, path]);
  if (!src) return (
    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f1f5f9', border: '2px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}>
      <User size={15} />
    </div>
  );
  return (
    <img
      src={src}
      alt="顔写真"
      onClick={e => { e.stopPropagation(); onOpen(src); }}
      style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', cursor: 'pointer', border: '2px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', transition: 'transform 0.15s' }}
      onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.15)')}
      onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
    />
  );
}

// ---- CSV / Excel ユーティリティ ----
const STATUS_LABEL: Record<string, string> = {
  completed: '完了', working: '勤務中', absent: '欠勤', edited: '修正済',
};

interface ExportColumn { label: string; width: number }

/** 従来の列構成（unified_work_types が無効な会社はこちら。変更しない） */
const EXPORT_COLUMNS: ExportColumn[] = [
  { label: '日付',               width: 14 },
  { label: '氏名',               width: 16 },
  { label: '社員コード',         width: 14 },
  { label: '船名',               width: 18 },
  { label: '業務開始',           width: 12 },
  { label: '業務終了',           width: 12 },
  { label: '勤務時間(分)',       width: 14 },
  { label: '残業時間(分)',       width: 14 },
  { label: 'アルコール値(mg/L)', width: 18 },
  { label: '作業種別',           width: 14 },
  { label: 'ステータス',         width: 12 },
];

/**
 * 統合表示（unified_work_types）の列構成。
 * 業務の区分は出さず「業務内容」ごとの1行として並べる。
 * オートフィルタやピボットで業務内容ごとの時間・件数を集計できる。
 */
const UNIFIED_EXPORT_COLUMNS: ExportColumn[] = [
  { label: '日付',               width: 14 },
  { label: '氏名',               width: 16 },
  { label: '社員コード',         width: 14 },
  { label: '船名',               width: 18 },
  { label: '業務内容',           width: 20 },
  { label: '開始',               width: 12 },
  { label: '終了',               width: 12 },
  { label: '時間(分)',           width: 12 },
  { label: '残業時間(分)',       width: 14 },
  { label: 'アルコール値(mg/L)', width: 18 },
  { label: 'ステータス',         width: 12 },
  { label: 'メモ',               width: 26 },
];

/** 統合出力の注記（時間を二重に数えないための説明を帳票に残す） */
const UNIFIED_EXPORT_NOTE =
  '※1行＝1件の業務です。「業務内容」で絞り込むと、その作業の時間と件数（回数）を集計できます。'
  + '勤務時間の中で行った作業は勤務時間にも含まれるため、業務内容をまたいで時間を単純に合計しないでください。';

function toCsvRow(row: string[]): string {
  return row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',');
}

function downloadCsv(columns: ExportColumn[], rows: string[][], filename: string) {
  const csv = [toCsvRow(columns.map(c => c.label)), ...rows.map(toCsvRow)].join('\r\n');
  // Excel用 UTF-8 BOM付き
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

async function downloadExcel(
  columns: ExportColumn[],
  rows: (string | number | null | undefined)[][],
  filename: string,
  periodLabel?: string,
  note?: string,
) {
  const colCount = columns.length;
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('勤怠');

  ws.columns = columns.map(h => ({ width: h.width }));

  // Title row
  const titleRow = ws.addRow([periodLabel ? `勤怠一覧　${periodLabel}` : '勤怠一覧']);
  titleRow.height = 28;
  styleTitle(titleRow, colCount);
  ws.mergeCells(titleRow.number, 1, titleRow.number, colCount);

  // 注記行（統合出力のときだけ。時間の数え方の注意を帳票に残す）
  if (note) {
    const noteRow = ws.addRow([note]);
    styleNoteRow(noteRow, colCount);
    ws.mergeCells(noteRow.number, 1, noteRow.number, colCount);
  }

  // Header row
  const headerRow = ws.addRow(columns.map(h => h.label));
  headerRow.height = 22;
  styleHeader(headerRow, colCount);

  // Data rows
  rows.forEach((r, i) => {
    const dataRow = ws.addRow(r.map(v => v ?? ''));
    dataRow.height = 18;
    styleDataRow(dataRow, i, colCount);
  });

  // 統合出力は区分で絞り込んで集計するため、見出し固定＋オートフィルターを付ける
  if (note) {
    ws.views = [{ state: 'frozen', ySplit: headerRow.number }];
    ws.autoFilter = {
      from: { row: headerRow.number, column: 1 },
      to:   { row: headerRow.number, column: colCount },
    };
  }

  await downloadWorkbook(wb, filename);
}

function recordToRow(r: AttendanceRecord): string[] {
  return [
    r.date, r.crewName, '', r.vessel,
    r.clockIn, r.clockOut,
    String(r.workMin), String(r.overMin),
    r.alcoholValue != null ? r.alcoholValue.toFixed(3) : '',
    r.workType ?? '',
    STATUS_LABEL[r.status] ?? r.status,
  ];
}

/**
 * 統合出力の行（出退勤の記録のあとに、その日の時間指定の作業を同じ表へ並べる）。
 * 区分は出さず、どの行も「業務内容」で識別する。1行＝1件なので件数＝回数として数えられる。
 */
function unifiedRows(
  records: AttendanceRecord[],
  activityBySession: Record<string, ActivityLogEntry[]>,
): string[][] {
  const rows: string[][] = [];
  for (const r of records) {
    const logs = activityBySession[r.id] ?? [];
    // 作業ログだけを保持する入れ物セッションは出退勤の行を出さない（0時間の行が並ぶのを防ぐ）
    if (!(r.isActivityOnly && logs.length > 0)) {
      rows.push([
        r.date, r.crewName, r.employeeCode ?? '', r.vessel,
        r.workType ?? '',
        r.clockIn, r.clockOut,
        String(r.workMin), String(r.overMin),
        r.alcoholValue != null ? r.alcoholValue.toFixed(3) : '',
        STATUS_LABEL[r.status] ?? r.status, '',
      ]);
    }
    for (const log of logs) {
      rows.push([
        jstDateOf(log.started_at) || r.date, r.crewName, r.employeeCode ?? '', r.vessel,
        log.name,
        fmtLocal(log.started_at), fmtLocal(log.ended_at),
        String(log.duration_minutes), '', '',
        '完了', log.note ?? '',
      ]);
    }
  }
  return rows;
}

type RecordSource = 'gps_auto' | 'admin_input' | 'crew_edit';

interface EditHistory {
  editedAt: string;
  editedBy: string;
  role: string;
  userRole?: string;
  field: string;
  before: string;
  after: string;
  reason?: string;
}

interface AttendanceRecord {
  id: string;
  userId: string;
  crewName: string;
  employeeCode?: string;
  /** 臨時業務だけを保持する入れ物セッション（通常の出退勤が無い） */
  isActivityOnly?: boolean;
  /** 管理者の操作（船員デモ・配乗の無い管理者の打刻）。一覧には印付きで出すが、合計・内訳・出力には含めない */
  adminOperated?: boolean;
  vessel: string;
  vessel_id: string;
  date: string;
  clockIn: string;
  clockOut: string;
  clockInRaw: string;
  clockOutRaw: string;
  breakMin: number;
  workMin: number;
  overMin: number;
  workType?: string;
  status: 'completed' | 'working' | 'absent' | 'edited';
  recordSource: RecordSource;
  editHistory: EditHistory[];
  note?: string;
  alcoholValue?: number;
  faceImagePath?: string;
  authMethod?: string;
}

function fmtLocal(isoStr: string): string {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return isoStr;
  return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tokyo', hour12: false });
}

function fmtMin(min: number): string {
  if (!min) return '-';
  const h = Math.floor(min / 60), m = min % 60;
  return m > 0 ? h + 'h' + m + 'm' : h + 'h';
}

function toRecord(r: any): AttendanceRecord {
  return {
    id:           String(r.id ?? ''),
    crewName:     String(r.user_name ?? r.crewName ?? r.crew_name ?? ''),
    adminOperated: Number(r.admin_operated ?? 0) === 1,
    vessel:       String(r.vessel_name ?? r.vessel ?? ''),
    date:         String(r.work_date ?? r.date ?? '').slice(0, 10),
    clockIn:      fmtLocal(r.clock_in ?? r.clockIn ?? ''),
    clockOut:     fmtLocal(r.clock_out ?? r.clockOut ?? ''),
    clockInRaw:   String(r.clock_in ?? r.clockIn ?? ''),
    clockOutRaw:  String(r.clock_out ?? r.clockOut ?? ''),
    breakMin:     Number(r.break_minutes ?? r.breakMin ?? 60),
    workMin:      Number(r.work_minutes ?? r.workMin ?? 0),
    overMin:      Number(r.over_minutes ?? r.overMin ?? 0),
    status:       (r.status ?? 'completed') as AttendanceRecord['status'],
    recordSource: (() => {
      try {
        const h = JSON.parse(r.note ?? '[]');
        if (Array.isArray(h) && h.length > 0) {
          return (h[h.length - 1] as any).role === 'crew' ? 'crew_edit' : 'admin_input';
        }
      } catch (err) { console.error('[AttendancePage]', err); }
      return 'gps_auto' as RecordSource;
    })(),
    editHistory: (() => {
      try {
        const h = JSON.parse(r.note ?? '[]');
        if (Array.isArray(h)) return h.map((e: any) => ({
          editedAt: new Date(e.at).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' }).slice(0, 16),
          editedBy: e.by ?? '不明',
          role: e.role ?? 'admin',
          userRole: e.user_role,
          field: '打刻修正',
          before: e.before ?? '',
          after:  e.after  ?? '',
          reason: e.reason,
        }));
      } catch (err) { console.error('[AttendancePage]', err); }
      return [];
    })(),
    userId:         String(r.user_id ?? ''),
    employeeCode:   r.employee_code != null ? String(r.employee_code) : undefined,
    // 入れ物セッションは開始＝終了・勤務0分で作られる。
    // 一覧API(/attendance)は is_activity_only を返さないことがあるため、その形からも判定する。
    isActivityOnly: r.is_activity_only
      ? true
      : (Number(r.work_minutes ?? r.workMin ?? 0) === 0
         && !!(r.clock_in ?? r.clockIn)
         && String(r.clock_in ?? r.clockIn) === String(r.clock_out ?? r.clockOut ?? '')),
    vessel_id:      String(r.vessel_id ?? ''),
    workType:       r.work_type ?? undefined,
    note:           r.note ?? '',
    alcoholValue:   r.alcohol_value != null ? Number(r.alcohol_value) : undefined,
    faceImagePath:  r.face_image_path ?? undefined,
    authMethod:     r.auth_method ?? undefined,
  };
}

// ---- helpers ----
function jstDateOf(iso: string): string {
  if (!iso) return '';
  return new Date(new Date(iso).getTime() + 9 * 3600000).toISOString().slice(0, 10);
}
function addOneDay(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

/** 作業内容ごとの内訳表示用。1時間未満は「45分」、以上は「1h30m」 */
function fmtDur(min: number): string {
  if (!min) return '-';
  return min < 60 ? `${min}分` : fmtMin(min);
}

// ---- 時間を指定して記録した作業（activity logs）の集計ヘルパー ----
// unified_work_types が有効な会社では、出退勤の業務も作業ログも1つの「業務」として扱う。
// 画面には作業名ごとの合計時間と回数を出し、区分（通常/臨時）という概念は出さない。

/** セッションに紐づく作業ログの合計時間（分） */
function activityMin(logs: ActivityLogEntry[]): number {
  return logs.reduce((s, l) => s + (l.duration_minutes ?? 0), 0);
}

/**
 * 勤務時間帯の外で行われた臨時業務の合計（分）。
 * 勤務中に行った臨時業務はすでに勤務時間に含まれるため、そのまま足すと二重計上になる。
 * 業務時間には「はみ出した分」だけを加算する。
 */
/** 労働区分の3本のどれに入るか（労働／別枠／労働外）。区分の無い記録は「労働」 */
function bucketOfLog(l: Pick<ActivityLogEntry, 'counts_as_work' | 'counts_toward_limit' | 'permits_over_limit'>): 'work' | 'exempt' | 'nonwork' {
  if (l.counts_as_work === 0) return 'nonwork';
  if (l.counts_toward_limit === 0 || l.permits_over_limit === 1) return 'exempt';
  return 'work';
}

/** 作業記録1件の「勤務時間帯と重なる分」（分） */
function overlapWithSession(rec: AttendanceRecord, l: ActivityLogEntry): number {
  const ws = rec.clockInRaw  ? Date.parse(rec.clockInRaw)  : NaN;
  const we = rec.clockOutRaw ? Date.parse(rec.clockOutRaw) : NaN;
  const ls = Date.parse(l.started_at), le = Date.parse(l.ended_at);
  if (isNaN(ws) || isNaN(we) || we <= ws || isNaN(ls) || isNaN(le)) return 0;
  return Math.round(Math.max(0, Math.min(we, le) - Math.max(ws, ls)) / 60000);
}

function activityExtraMin(rec: AttendanceRecord, logs: ActivityLogEntry[]): number {
  if (logs.length === 0) return 0;
  let extra = 0;
  for (const l of logs) {
    // 労働外（食事・自発的作業…）は労働時間に足さない
    if (bucketOfLog(l) === 'nonwork') continue;
    const dur = l.duration_minutes ?? 0;
    extra += Math.max(0, dur - overlapWithSession(rec, l));
  }
  return extra;
}

/** 労働区分の内訳: 別枠（68条・64条）と労働外（勤務中に行った分は勤務時間から引く） */
function laborBucketsOf(rec: AttendanceRecord, logs: ActivityLogEntry[]): { exemptMin: number; safetyOtMin: number; nonWorkMin: number; nonWorkInsideMin: number } {
  const r = { exemptMin: 0, safetyOtMin: 0, nonWorkMin: 0, nonWorkInsideMin: 0 };
  for (const l of logs) {
    const b = bucketOfLog(l);
    const dur = l.duration_minutes ?? 0;
    if (b === 'nonwork') { r.nonWorkMin += dur; r.nonWorkInsideMin += Math.min(dur, overlapWithSession(rec, l)); }
    else if (b === 'exempt') { if (l.counts_toward_limit === 0) r.exemptMin += dur; if (l.permits_over_limit === 1) r.safetyOtMin += dur; }
  }
  return r;
}

/** 統合表示の業務時間（通常勤務＋勤務外の臨時業務。勤務中の労働外は差し引く） */
function unifiedWorkMin(rec: AttendanceRecord, logs: ActivityLogEntry[]): number {
  return Math.max(0, rec.workMin + activityExtraMin(rec, logs) - laborBucketsOf(rec, logs).nonWorkInsideMin);
}

/** 臨時業務だけの記録を1つの業務として見せるための時刻（最初の開始〜最後の終了） */
function activitySpan(logs: ActivityLogEntry[]): { start: string; end: string } {
  const starts = logs.map(l => Date.parse(l.started_at)).filter(n => !isNaN(n));
  const ends   = logs.map(l => Date.parse(l.ended_at)).filter(n => !isNaN(n));
  if (starts.length === 0 || ends.length === 0) return { start: '', end: '' };
  return {
    start: fmtLocal(new Date(Math.min(...starts)).toISOString()),
    end:   fmtLocal(new Date(Math.max(...ends)).toISOString()),
  };
}

// ---- Edit Modal ----
const DEFAULT_WORK_TYPE_OPTIONS = ['航海','荷役','当直','点検・整備','係船作業','清掃','訓練','その他'];

/** 船舶の業務種別マスタを選択肢にする（マスタ未設定・取得失敗時は既定8種） */
function useVesselWorkTypeOptions(vesselId?: string): string[] {
  const [options, setOptions] = useState<string[]>(DEFAULT_WORK_TYPE_OPTIONS);
  useEffect(() => {
    let alive = true;
    if (!vesselId) { setOptions(DEFAULT_WORK_TYPE_OPTIONS); return; }
    api.get<any>(`/vessels/${vesselId}/work-types`).then(res => {
      const names: string[] = (res?.data ?? []).map((w: any) => String(w?.name ?? '')).filter(Boolean);
      if (alive) setOptions(names.length ? names : DEFAULT_WORK_TYPE_OPTIONS);
    }).catch(() => { if (alive) setOptions(DEFAULT_WORK_TYPE_OPTIONS); });
    return () => { alive = false; };
  }, [vesselId]);
  return options;
}

function EditModal({ record, onSave, onClose }: {
  record: AttendanceRecord;
  onSave: (updated: AttendanceRecord, history: EditHistory, clockOutDate?: string, clockInDate?: string) => void;
  onClose: () => void;
}) {
  const [clockInDate,  setClockInDate]  = useState(record.date);
  const [clockIn,      setClockIn]      = useState(record.clockIn);
  const [clockOutDate, setClockOutDate] = useState(() =>
    record.clockOutRaw ? jstDateOf(record.clockOutRaw) : record.date
  );
  const [clockOut,  setClockOut]  = useState(record.clockOut);
  const [workType,  setWorkType]  = useState(record.workType ?? '');
  const [reason,    setReason]    = useState('');
  const [error,     setError]     = useState('');

  const WORK_TYPE_OPTIONS = useVesselWorkTypeOptions(record.vessel_id || undefined);
  const origClockOutDate = record.clockOutRaw ? jstDateOf(record.clockOutRaw) : record.date;

  const getMs = (date: string, time: string) =>
    date && time ? new Date(`${date}T${time}:00+09:00`).getTime() : null;

  const isTimeValid = () => {
    const s = getMs(clockInDate, clockIn), e = getMs(clockOutDate, clockOut);
    return !s || !e || e > s;
  };

  const calcWork = () => {
    const s = getMs(clockInDate, clockIn), e = getMs(clockOutDate, clockOut);
    if (!s || !e || e <= s) return 0;
    return Math.max(0, Math.round((e - s) / 60000));
  };

  const handleSave = () => {
    if (!reason.trim()) { setError('修正理由を入力してください'); return; }
    if (!isTimeValid()) { setError('業務終了は業務開始より後である必要があります'); return; }
    const changes: string[] = [];
    if (clockIn     !== record.clockIn)         changes.push('業務開始時刻変更');
    if (clockInDate !== record.date)            changes.push('業務開始日変更');
    if (clockOut    !== record.clockOut)        changes.push('業務終了時刻変更');
    if (clockOutDate !== origClockOutDate)       changes.push('業務終了日変更');
    if (workType !== (record.workType ?? ''))    changes.push('作業種別変更');
    if (changes.length === 0) { setError('変更がありません'); return; }
    const workMin = calcWork();
    const overMin = Math.max(0, workMin - 480);
    const history: EditHistory = {
      editedAt: new Date().toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' }).slice(0, 16),
      editedBy: '管理者',
      role: 'admin',
      field: changes.join(' / '),
      before: `${record.date} ${record.clockIn} → ${origClockOutDate} ${record.clockOut} / ${record.workType ?? '未設定'}`,
      after:  `${clockInDate} ${clockIn} → ${clockOutDate} ${clockOut} / ${workType || '未設定'}`,
      reason,
    };
    const coDate = clockOutDate !== clockInDate ? clockOutDate : undefined;
    const ciDate = clockInDate !== record.date ? clockInDate : undefined;
    onSave({ ...record, clockIn, clockOut, workMin, overMin, workType: workType || undefined, recordSource: 'admin_input', status: 'edited' }, history, coDate, ciDate);
  };

  const wm = calcWork();
  const valid = isTimeValid();
  const showTimeError = !valid && !!(clockIn && clockOut);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>勤怠を修正する</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{record.crewName}　{record.vessel}</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>

        {/* 業務開始 */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 5 }}>業務開始</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <SmartDateInput value={clockInDate} onChange={setClockInDate} style={{ flex: 1 }} />
            <SmartTimeInput value={clockIn} onChange={setClockIn} style={{ flex: 1 }} />
          </div>
        </div>

        {/* 業務終了 */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 5 }}>業務終了</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <SmartDateInput value={clockOutDate} onChange={setClockOutDate} style={{ flex: 1 }} />
            <SmartTimeInput value={clockOut} onChange={setClockOut} style={{ flex: 1 }} />
          </div>
          {showTimeError && (
            <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4, fontWeight: 600 }}>業務終了は業務開始より後である必要があります</div>
          )}
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 12 }}>
          作業種別
          <select value={workType} onChange={e => setWorkType(e.target.value)}
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', background: '#fff', color: '#0f172a' }}>
            <option value="">（未設定）</option>
            {workType && !WORK_TYPE_OPTIONS.includes(workType) && <option value={workType}>{workType}</option>}
            {WORK_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        {clockIn && clockOut && valid && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#166534' }}>
            勤務時間: <strong>{fmtMin(wm)}</strong>
            {wm > 480 && <span style={{ color: '#dc2626', marginLeft: 10 }}>残業 +{fmtMin(wm - 480)}</span>}
          </div>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#1d4ed8', fontWeight: 700, marginBottom: 8 }}>
          修正理由（必須）
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
            placeholder="例: 打刻漏れのため管理者修正"
            style={{ border: '1.5px solid #bfdbfe', borderRadius: 8, padding: '9px 12px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', outline: 'none' }} />
        </label>
        {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>キャンセル</button>
          <button onClick={handleSave} style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 10, background: '#1d4ed8', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>保存する</button>
        </div>
      </div>
    </div>
  );
}

// ---- Detail Modal（詳細 / 編集タブ）----
interface SessionDetail {
  session: any;
  clock_in: any | null;
  clock_out: any | null;
}
function DetailPhoto({ apiBase, path, label, onOpen }: { apiBase: string; path?: string | null; label: string; onOpen: (url: string) => void; }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!path) { setSrc(null); return; }
    const token = api.getToken() ?? '';
    let obj: string | null = null;
    fetch(`${apiBase}/faces/${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.blob() : Promise.reject())
      .then(b => { obj = URL.createObjectURL(b); setSrc(obj); }).catch(() => {});
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [apiBase, path]);
  return (
    <div style={{ flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 5 }}>{label}</div>
      {src ? (
        <img src={src} alt={label} onClick={() => onOpen(src)}
          style={{ width: '100%', maxWidth: 160, aspectRatio: '3/4', objectFit: 'cover', borderRadius: 10, border: '1px solid #e2e8f0', cursor: 'zoom-in', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} />
      ) : (
        <div style={{ width: '100%', maxWidth: 160, aspectRatio: '3/4', borderRadius: 10, background: '#f8fafc', border: '1px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', fontSize: 12 }}>写真なし</div>
      )}
    </div>
  );
}
function DetailModal({ record, apiBase, onClose, onEdit, onOpenPhoto }: {
  record: AttendanceRecord;
  apiBase: string;
  onClose: () => void;
  onEdit: () => void;
  onOpenPhoto: (url: string) => void;
}) {
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  // 判定基準は会社ごとに設定できる（アラート設定）。画面に数値を直接書かない。
  const alcoTh = useAlcoholThresholds();
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get<{ data: SessionDetail }>(`/timestamps/admin/sessions/${record.id}`)
      .then(r => { if (alive) setDetail(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [record.id]);

  const ci = detail?.clock_in, co = detail?.clock_out, s = detail?.session;
  const gmap = (lat?: number, lng?: number) => (lat != null && lng != null)
    ? `https://www.google.com/maps?q=${lat},${lng}` : null;
  const coord = (lat?: number, lng?: number, acc?: number) => (lat != null && lng != null)
    ? `${lat.toFixed(5)}, ${lng.toFixed(5)}${acc != null ? `（±${Math.round(acc)}m）` : ''}` : '記録なし';
  const authLabel = (m?: string) => m === 'face_photo' ? '顔写真' : m === 'id_password' ? 'ID/パスワード' : (m || '-');
  const alco = record.alcoholValue;
  const alcoJudge = alco == null ? null : judgeAlcohol(alco, alcoTh);
  const alcoStyle = alcoJudge == null ? null
    : alcoJudge === 'normal'  ? { label: '正常', color: '#059669', bg: '#f0fdf4' }
    : alcoJudge === 'warning' ? { label: '要確認', color: '#d97706', bg: '#fffbeb' }
    : { label: '基準超過', color: '#dc2626', bg: '#fef2f2' };

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div style={{ display: 'flex', gap: 12, padding: '9px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
      <div style={{ width: 96, flexShrink: 0, color: '#64748b', fontWeight: 600 }}>{label}</div>
      <div style={{ flex: 1, color: '#0f172a' }}>{children}</div>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: 560, maxWidth: '96vw', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 12px' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{record.crewName}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{record.vessel || '-'}　{record.date}</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>
        {/* タブ */}
        <div style={{ display: 'flex', gap: 4, padding: '0 24px', borderBottom: '2px solid #e5e7eb' }}>
          <div style={{ padding: '10px 16px', fontSize: 14, fontWeight: 700, color: '#1d4ed8', borderBottom: '2px solid #1d4ed8', marginBottom: -2 }}>詳細</div>
          <button onClick={onEdit} style={{ padding: '10px 16px', fontSize: 14, fontWeight: 500, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
            <Pencil size={13} /> 編集
          </button>
        </div>

        <div style={{ padding: '16px 24px 24px' }}>
          {loading ? <div style={{ color: '#94a3b8', fontSize: 13, padding: '20px 0' }}>読み込み中...</div> : (
            <>
              {/* 本人確認写真 */}
              <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', margin: '4px 0 8px' }}>本人確認写真</div>
              <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
                <DetailPhoto apiBase={apiBase} path={ci?.face_image_path} label="打刻時（顔）" onOpen={onOpenPhoto} />
                <DetailPhoto apiBase={apiBase} path={ci?.alcohol_image_path} label="アルコール測定時" onOpen={onOpenPhoto} />
              </div>

              <Row label="業務開始">{record.clockIn || '-'}{s?.work_type_start_label ? <span style={{ marginLeft: 10, ...WORK_BADGE_STYLE(), borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>{s.work_type_start_label}</span> : null}</Row>
              <Row label="業務終了">{record.clockOut || <span style={{ color: '#1d4ed8', fontWeight: 700 }}>● 勤務中</span>}{s?.work_type_end_label ? <span style={{ marginLeft: 10, ...WORK_BADGE_STYLE(), borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>{s.work_type_end_label}</span> : null}</Row>
              <Row label="勤務時間">{fmtMin(record.workMin)}{record.overMin > 0 && <span style={{ color: '#dc2626', marginLeft: 10, fontWeight: 700 }}>残業 +{fmtMin(record.overMin)}</span>}</Row>
              <Row label="アルコール">
                {alco == null ? '記録なし' : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <strong>{alco.toFixed(3)} mg/L</strong>
                    {alcoStyle && <span style={{ background: alcoStyle.bg, color: alcoStyle.color, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>{alcoStyle.label}</span>}
                  </span>
                )}
              </Row>
              <Row label="認証方法">{authLabel(ci?.auth_method)}</Row>
              <Row label="打刻場所（開始）">
                {coord(ci?.latitude, ci?.longitude, ci?.accuracy)}
                {gmap(ci?.latitude, ci?.longitude) && <a href={gmap(ci?.latitude, ci?.longitude)!} target="_blank" rel="noreferrer" style={{ marginLeft: 8, color: '#0ea5e9', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}><MapPin size={12} />地図</a>}
              </Row>
              <Row label="打刻場所（終了）">
                {coord(co?.latitude, co?.longitude, co?.accuracy)}
                {gmap(co?.latitude, co?.longitude) && <a href={gmap(co?.latitude, co?.longitude)!} target="_blank" rel="noreferrer" style={{ marginLeft: 8, color: '#0ea5e9', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}><MapPin size={12} />地図</a>}
              </Row>
              {record.editHistory.length > 0 && (
                <Row label="修正履歴">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {record.editHistory.map((h, i) => (
                      <div key={i} style={{ fontSize: 12, color: '#475569' }}>
                        {h.editedAt}　{h.editedBy}（{h.role === 'crew' ? '本人' : '管理者'}）: {h.field}{h.reason ? `／${h.reason}` : ''}
                      </div>
                    ))}
                  </div>
                </Row>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button onClick={onClose} style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>閉じる</button>
                <button onClick={onEdit} style={{ flex: 1, padding: '11px', border: 'none', borderRadius: 10, background: '#1d4ed8', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Pencil size={14} />編集する</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- New Entry Modal ----
function NewEntryModal({ onSave, onClose, users, defaultDate, defaultUserId, onSavedWithActivity, autoActivity }: {
  onSave: () => void; onClose: () => void;
  users: { id: string; name: string; vessel: string; vesselId?: string }[];
  defaultDate?: string;
  defaultUserId?: string;
  onSavedWithActivity?: (sessionId: string, vesselId: string, workDate: string) => void;
  autoActivity?: boolean;
}) {
  const todayJst = () => new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);
  const [userId,       setUserId]       = useState(defaultUserId ?? (users[0]?.id ?? ''));
  const [workDate,     setWorkDate]     = useState(defaultDate ?? todayJst);
  const [clockIn,      setClockIn]      = useState('09:00');
  const [clockOutDate, setClockOutDate] = useState(defaultDate ?? todayJst);
  const [clockOut,     setClockOut]     = useState('18:00');
  const [workType,     setWorkType]     = useState('');
  const [reason,       setReason]       = useState('');
  const [error,        setError]        = useState('');
  const [saving,       setSaving]       = useState(false);
  const [savedResult,  setSavedResult]  = useState<{ id: string; vessel_id: string; work_date: string } | null>(null);
  const WORK_TYPE_OPTIONS = useVesselWorkTypeOptions(users.find(u => u.id === userId)?.vesselId || undefined);

  const getMs = (date: string, time: string) =>
    date && time ? new Date(`${date}T${time}:00+09:00`).getTime() : null;

  const isTimeValid = () => {
    const s = getMs(workDate, clockIn), e = getMs(clockOutDate, clockOut);
    return !s || !e || e > s;
  };

  const calcWork = () => {
    const s = getMs(workDate, clockIn), e = getMs(clockOutDate, clockOut);
    if (!s || !e || e <= s) return null;
    return Math.max(0, Math.round((e - s) / 60000));
  };

  const handleSave = async () => {
    if (!reason.trim()) { setError('理由を入力してください'); return; }
    if (!userId || !workDate || !clockIn) { setError('乗組員・日付・出勤時刻は必須です'); return; }
    if (clockOut && !isTimeValid()) { setError('業務終了は業務開始より後である必要があります'); return; }
    setSaving(true); setError('');
    try {
      const res = await api.post<{ data: { id: string; vessel_id: string; work_date: string } }>('/attendance', {
        user_id: userId, work_date: workDate,
        clock_in: clockIn,
        clock_out: clockOut || undefined,
        clock_out_date: (clockOut && clockOutDate !== workDate) ? clockOutDate : undefined,
        work_type: workType || undefined,
        reason,
      });
      if (autoActivity && onSavedWithActivity) {
        onSave();
        onSavedWithActivity(res.data.id, res.data.vessel_id ?? '', res.data.work_date ?? workDate);
      } else if (onSavedWithActivity) {
        setSavedResult({ id: res.data.id, vessel_id: res.data.vessel_id ?? '', work_date: res.data.work_date ?? workDate });
      } else {
        onSave();
      }
    } catch (e: any) { setError(e.message ?? '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  const valid = isTimeValid();
  const showTimeError = !valid && !!(clockIn && clockOut);
  const wm = calcWork();

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>勤怠を手動追加</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>打刻漏れ等の記録を手動で追加します</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 12 }}>
          乗組員
          <select value={userId} onChange={e => setUserId(e.target.value)}
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none' }}>
            {(() => {
              const map = new Map<string, typeof users>();
              for (const u of users) {
                const key = u.vessel || '';
                if (!map.has(key)) map.set(key, []);
                map.get(key)!.push(u);
              }
              const groups = [...map.entries()].map(([v, ms]) => ({ vessel: v, members: ms }));
              groups.sort((a, b) => (!a.vessel && b.vessel) ? 1 : (a.vessel && !b.vessel) ? -1 : a.vessel.localeCompare(b.vessel, 'ja'));
              return groups.map(({ vessel, members }) =>
                vessel ? (
                  <optgroup key={vessel} label={`🚢 ${vessel}`}>
                    {members.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </optgroup>
                ) : (
                  <optgroup key="__none__" label="（未配属）">
                    {members.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </optgroup>
                )
              );
            })()}
          </select>
        </label>

        {/* 業務開始 */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 5 }}>業務開始</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <SmartDateInput value={workDate} onChange={d => { if (clockOutDate === workDate) setClockOutDate(d); setWorkDate(d); }} style={{ flex: 1 }} />
            <SmartTimeInput value={clockIn} onChange={setClockIn} style={{ flex: 1 }} />
          </div>
        </div>

        {/* 業務終了 */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 5 }}>業務終了</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <SmartDateInput value={clockOutDate} onChange={setClockOutDate} style={{ flex: 1 }} />
            <SmartTimeInput value={clockOut} onChange={setClockOut} style={{ flex: 1 }} />
          </div>
          {showTimeError && (
            <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4, fontWeight: 600 }}>業務終了は業務開始より後である必要があります</div>
          )}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600 }}>
            作業種別
            <select value={workType} onChange={e => setWorkType(e.target.value)}
              style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', background: '#fff', color: '#0f172a' }}>
              <option value="">（未設定）</option>
              {workType && !WORK_TYPE_OPTIONS.includes(workType) && <option value={workType}>{workType}</option>}
              {WORK_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
        </div>
        {wm !== null && wm > 0 && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#166534' }}>
            勤務時間: <strong>{fmtMin(wm)}</strong>
            {wm > 480 && <span style={{ color: '#dc2626', marginLeft: 10 }}>残業 +{fmtMin(wm - 480)}</span>}
          </div>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#059669', fontWeight: 700, marginBottom: 8 }}>
          追加理由（必須）
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
            placeholder="例: 打刻漏れのため管理者追加"
            style={{ border: '1.5px solid #bbf7d0', borderRadius: 8, padding: '9px 12px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', outline: 'none' }} />
        </label>
        {savedResult ? (
          <div style={{ marginTop: 18 }}>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px', marginBottom: 14, color: '#166534', fontSize: 14, fontWeight: 600 }}>
              ✓ 勤怠を追加しました
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onSave} style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>閉じる</button>
              {onSavedWithActivity && (
                <button onClick={() => { onSave(); onSavedWithActivity(savedResult.id, savedResult.vessel_id, savedResult.work_date); }}
                  style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 10, background: actAccent().fg, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                  {isUnified() ? '作業を追加する' : '特別作業を追加する'}
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button onClick={onClose} style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>キャンセル</button>
              <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 10, background: saving ? '#6ee7b7' : '#059669', color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
                {saving ? '保存中...' : '追加する'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---- 臨時業務（統合表示では単に「作業」）のエントリーピッカー ----
function SpecialActivityPickerModal({ users, onSubmit, onClose }: {
  users: { id: string; name: string; vessel: string }[];
  onSubmit: (userId: string, date: string) => void;
  onClose: () => void;
}) {
  const todayJst = new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);
  const [userId, setUserId] = useState(users[0]?.id ?? '');
  const [date, setDate]   = useState(todayJst);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 400, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{isUnified() ? '作業を追加' : '特別作業を追加'}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>乗組員と日付を選択してください</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 14 }}>
          乗組員
          <select value={userId} onChange={e => setUserId(e.target.value)}
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none' }}>
            {(() => {
              const map = new Map<string, typeof users>();
              for (const u of users) { const k = u.vessel || ''; if (!map.has(k)) map.set(k, []); map.get(k)!.push(u); }
              const groups = [...map.entries()].map(([v, ms]) => ({ vessel: v, members: ms }));
              groups.sort((a, b) => (!a.vessel && b.vessel) ? 1 : (a.vessel && !b.vessel) ? -1 : a.vessel.localeCompare(b.vessel, 'ja'));
              return groups.map(({ vessel, members }) =>
                vessel ? (
                  <optgroup key={vessel} label={`🚢 ${vessel}`}>{members.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</optgroup>
                ) : (
                  <optgroup key="__none__" label="（未配属）">{members.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</optgroup>
                )
              );
            })()}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 20 }}>
          日付
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none' }} />
        </label>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 11, border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>キャンセル</button>
          <button onClick={() => { onSubmit(userId, date); onClose(); }}
            style={{ flex: 2, padding: 11, border: 'none', borderRadius: 10, background: actAccent().fg, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
            次へ →
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- History Panel ----
function HistoryPanel({ record, onClose }: { record: AttendanceRecord; onClose: () => void }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 540, maxWidth: '95vw', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>修正履歴</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{record.crewName}　{record.vessel}　{record.date}</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>
        {record.editHistory.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: 24, fontSize: 14 }}>修正履歴はありません</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {record.editHistory.map((h, i) => {
              const roleLabel = (() => {
                if (h.userRole === 'system_admin') return { label: 'システム管理者', color: '#dc2626', bg: '#fee2e2' };
                if (h.userRole === 'shore_admin')  return { label: '陸上管理者',     color: '#1d4ed8', bg: '#dbeafe' };
                if (h.role === 'admin')            return { label: '管理者',         color: '#1d4ed8', bg: '#dbeafe' };
                return                                    { label: '乗組員本人',     color: '#7c3aed', bg: '#ede9fe' };
              })();
              return (
                <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', background: '#fafafa' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ background: roleLabel.bg, color: roleLabel.color, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
                        {roleLabel.label}
                      </span>
                      <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{h.editedBy}</span>
                    </div>
                    <span style={{ color: '#94a3b8', fontSize: 12 }}>{h.editedAt}</span>
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                    <span style={{ color: '#dc2626', fontWeight: 600 }}>変更前:</span> {h.before}<br />
                    <span style={{ color: '#059669', fontWeight: 600 }}>変更後:</span> {h.after}
                  </div>
                  {h.reason && <div style={{ background: '#fefce8', border: '1px solid #fef08a', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#854d0e', marginTop: 8 }}>💬 {h.reason}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── 臨時業務モーダル（統合表示では「作業の記録」） ─────────────────────
interface ActivityMasterItem { id: string; name: string; emoji: string; color: string; }
interface ActivityLogEntry {
  id: string; name: string; emoji: string; color: string;
  started_at: string; ended_at: string; duration_minutes: number; note?: string;
  /** 労働区分（機能フラグ labor_categories）。扱いはサーバーが区分マスタから付けて返す */
  labor_category?: string | null; labor_category_name?: string | null;
  counts_as_work?: number; counts_toward_limit?: number; permits_over_limit?: number; counts_as_rest?: number;
  reason?: string | null;
}

function ActivityLogModal({ record, onClose }: {
  record: AttendanceRecord;
  onClose: () => void;
}) {
  const [activityTypes, setActivityTypes] = React.useState<ActivityMasterItem[]>([]);
  const [existingLogs, setExistingLogs] = React.useState<ActivityLogEntry[]>([]);
  const [selected, setSelected] = React.useState('');
  const [startDate, setStartDate] = React.useState(record.date); // 開始日（既定は対象日）
  const [startTime, setStartTime] = React.useState('');
  const [endDate, setEndDate] = React.useState(record.date);     // 終了日（日跨ぎ時は翌日等を指定）
  const [endTime, setEndTime] = React.useState('');
  const [note, setNote] = React.useState('');
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [deleteTarget, setDeleteTarget] = React.useState<string | null>(null);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [typesRes, logsRes] = await Promise.all([
          api.get<any>(`/vessels/${(record as any).vessel_id ?? ''}/activity-types`),
          api.get<any>('/activity-logs', { work_session_id: record.id }),
        ]);
        setActivityTypes((typesRes?.data ?? []).filter((t: any) => t.is_active !== 0).map((t: any) => t.master ?? t));
        setExistingLogs(logsRes?.data ?? []);
      } catch (err) { console.error('[AttendancePage]', err); }
      finally { setLoading(false); }
    };
    load();
  }, [record.id]);

  const handleSave = async () => {
    if (!selected) { setError('作業種別を選択してください'); return; }
    if (!startDate || !startTime || !endDate || !endTime) { setError('開始・終了の日付と時刻を入力してください'); return; }
    const startIso = `${startDate}T${startTime}:00+09:00`;
    const endIso   = `${endDate}T${endTime}:00+09:00`;
    if (new Date(endIso) <= new Date(startIso)) { setError('終了は開始より後にしてください（日をまたぐ場合は終了日を翌日に）'); return; }
    setSaving(true); setError('');
    try {
      await api.post('/activity-logs', {
        work_session_id: record.id,
        vessel_id: (record as any).vessel_id ?? '',
        activity_master_id: selected,
        started_at: startIso,
        ended_at: endIso,
        note: note || undefined,
      });
      const res = await api.get<any>('/activity-logs', { work_session_id: record.id });
      setExistingLogs(res?.data ?? []);
      setSelected(''); setStartTime(''); setEndTime(''); setStartDate(record.date); setEndDate(record.date); setNote('');
    } catch (e: any) { setError(e.message ?? '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.del(`/activity-logs/${id}`);
      setExistingLogs(p => p.filter(l => l.id !== id));
    } catch (err) { console.error('[AttendancePage]', err); }
    setDeleteTarget(null);
  };

  const fmtIso = (iso: string) => {
    try { return new Date(iso).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tokyo', hour12: false }); } catch { return iso; }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 520, maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{actLabel.modal()}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{record.crewName}　{record.vessel}　{record.date}</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>

        {existingLogs.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>{actLabel.list()}</div>
            {existingLogs.map(log => (
              <div key={log.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#f8fafc', borderRadius: 10, marginBottom: 6, border: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: 22 }}>{log.emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{log.name}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{fmtIso(log.started_at)} → {fmtIso(log.ended_at)}（{log.duration_minutes}分）</div>
                  {log.note && <div style={{ fontSize: 11, color: '#94a3b8' }}>{log.note}</div>}
                </div>
                <button onClick={() => setDeleteTarget(log.id)} style={{ fontSize: 11, padding: '5px 10px', border: '1px solid #fecaca', borderRadius: 6, background: '#fff', color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}>削除</button>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>読み込み中...</div>
        ) : activityTypes.length === 0 ? (
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px', textAlign: 'center', color: '#64748b', fontSize: 13 }}>
            {isUnified() ? 'この船舶に時間を指定して記録する作業が設定されていません。' : 'この船舶に特別作業が設定されていません。'}<br />マスタ設定から追加してください。
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>作業を追加</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
              {activityTypes.map((t) => (
                <button key={t.id} onClick={() => setSelected(t.id)}
                  style={{ padding: '10px 8px', borderRadius: 10, border: selected === t.id ? `2px solid ${t.color}` : '2px solid #e2e8f0', background: selected === t.id ? t.color + '18' : '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer', position: 'relative' }}>
                  {selected === t.id && <div style={{ position: 'absolute', top: 5, right: 5, width: 14, height: 14, borderRadius: '50%', background: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4"><polyline points="20 6 9 17 4 12"/></svg></div>}
                  <span style={{ fontSize: 18 }}>{t.emoji}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#0f172a', textAlign: 'center' }}>{t.name}</span>
                </button>
              ))}
            </div>
            {/* 開始・終了とも日付＋時刻を指定（日をまたぐ特別作業に対応） */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 5 }}>開始</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <SmartDateInput value={startDate} onChange={setStartDate} style={{ flex: 1 }} />
                <SmartTimeInput value={startTime} onChange={setStartTime} style={{ flex: 1 }} />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 5 }}>終了</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <SmartDateInput value={endDate} onChange={setEndDate} style={{ flex: 1 }} />
                <SmartTimeInput value={endTime} onChange={setEndTime} style={{ flex: 1 }} />
              </div>
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 14 }}>
              メモ（任意）
              <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="例：荷役中の油水分離機確認"
                style={{ border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 13, outline: 'none' }} />
            </label>
            {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>閉じる</button>
              <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 10, background: saving ? '#93c5fd' : actAccent().fg, color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
                {saving ? '登録中...' : '追加登録する'}
              </button>
            </div>
          </>
        )}

        {deleteTarget && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: 320 }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>{isUnified() ? 'この作業の記録を削除しますか？' : '特別作業を削除しますか？'}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setDeleteTarget(null)} style={{ flex: 1, padding: 12, border: '1px solid #e2e8f0', borderRadius: 10, cursor: 'pointer' }}>キャンセル</button>
                <button onClick={() => handleDelete(deleteTarget)} style={{ flex: 1, padding: 12, border: 'none', borderRadius: 10, background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>削除</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Main Page ----
// ─── アルコール測定が必要な勤怠の承認待ち（測定連動の勤怠 alcohol_linked_attendance） ─────────────
// 船員が個人アプリから事後に登録・修正した「当直」などの勤怠は、ここで承認してから勤怠へ反映する。
interface AlcoholRequest {
  id: string; user_id: string; user_name: string; employee_code: string; work_session_id: string | null;
  target_date: string; requested_clock_in: string | null; requested_clock_out: string | null; requested_clock_out_date: string | null;
  requested_work_type: string | null; reason: string; break_minutes: number; status: string; created_at: string;
  /** 本人が選んだ共有端末の測定記録（あれば承認の判断材料になる） */
  alcohol_check?: { measured_at: string; alcohol_value: number | null; is_manual: number } | null;
}
function AlcoholApprovalPanel({ onApplied }: { onApplied: () => void }) {
  const [rows, setRows] = useState<AlcoholRequest[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [comment, setComment] = useState<Record<string, string>>({});
  const load = useCallback(async () => {
    try {
      const r = await api.get<{ data: AlcoholRequest[] }>('/alcohol-workflow/requests', { status: 'pending' });
      setRows(r?.data ?? []);
    } catch { setRows([]); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, [load]);
  const review = async (r: AlcoholRequest, action: 'approve' | 'reject') => {
    if (action === 'reject' && !window.confirm(`${r.user_name} さんの申請を却下しますか？`)) return;
    setBusy(r.id);
    try {
      await api.put(`/alcohol-workflow/requests/${r.id}`, { action, comment: (comment[r.id] ?? '').trim() || undefined });
      await load();
      if (action === 'approve') onApplied();
    } catch (e: any) { alert(e?.message ?? '処理に失敗しました'); }
    setBusy(null);
  };
  if (rows.length === 0) return null;
  const fmtDate = (d: string) => `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;
  return (
    <div style={{ margin: '0 0 14px', background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 12, padding: '12px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ background: '#ea580c', color: '#fff', borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 800 }}>承認待ち {rows.length}件</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#9a3412' }}>アルコール測定が必要な勤務の事後登録・修正（個人アプリから）</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map(r => (
          <div key={r.id} style={{ background: '#fff', border: '1px solid #fed7aa', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 220 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{r.user_name} <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>{r.employee_code}</span></div>
              <div style={{ fontSize: 13, color: '#334155', marginTop: 2 }}>
                {fmtDate(r.target_date)}　{r.requested_clock_in ?? '--:--'} → {r.requested_clock_out ?? '--:--'}{r.requested_clock_out_date && r.requested_clock_out_date !== r.target_date ? '（翌日）' : ''}
                {r.requested_work_type && <span style={{ marginLeft: 8, background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '1px 8px', fontSize: 12, fontWeight: 700 }}>{r.requested_work_type}</span>}
                <span style={{ marginLeft: 8, fontSize: 11, color: '#64748b' }}>{r.work_session_id ? '修正' : '新規登録'}・休憩{r.break_minutes}分</span>
              </div>
              <div style={{ fontSize: 12, marginTop: 3 }}>
                {r.alcohol_check
                  ? <span style={{ color: '#166534', background: '#dcfce7', borderRadius: 6, padding: '1px 8px', fontWeight: 700 }}>
                      🧪 測定あり {new Date(r.alcohol_check.measured_at).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}　{r.alcohol_check.alcohol_value != null ? `${Number(r.alcohol_check.alcohol_value).toFixed(3)} mg/L` : '--'}{r.alcohol_check.is_manual === 1 ? '（手入力）' : ''}
                    </span>
                  : <span style={{ color: '#991b1b', background: '#fee2e2', borderRadius: 6, padding: '1px 8px', fontWeight: 700 }}>測定記録なし</span>}
              </div>
              {r.reason && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>理由: {r.reason}</div>}
            </div>
            <input value={comment[r.id] ?? ''} onChange={e => setComment(c => ({ ...c, [r.id]: e.target.value }))} placeholder="コメント（任意・本人に届きます）"
              style={{ flex: 1, minWidth: 180, border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 12 }} />
            <div style={{ display: 'flex', gap: 6 }}>
              <button disabled={busy === r.id} onClick={() => review(r, 'approve')}
                style={{ padding: '8px 16px', border: 'none', borderRadius: 8, background: '#059669', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>承認して反映</button>
              <button disabled={busy === r.id} onClick={() => review(r, 'reject')}
                style={{ padding: '8px 14px', border: '1px solid #fecaca', borderRadius: 8, background: '#fff', color: '#dc2626', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>却下</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AttendancePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // 判定基準は会社ごとに設定できる（アラート設定）。画面に数値を直接書かない。
  const alcoTh = useAlcoholThresholds();

  const todayJst = () => new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const nowJst = () => new Date(Date.now() + 9 * 3600000);

  const [records,       setRecords]       = useState<AttendanceRecord[]>([]);
  const [viewMode,      setViewMode]      = useState<'table' | 'timeline'>(() => {
    const v = searchParams.get('view');
    return (v === 'table' || v === 'timeline') ? v : 'table';
  });
  const [users,         setUsers]         = useState<{ id: string; name: string; vessel: string; vesselId?: string }[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [editTarget,       setEditTarget]       = useState<AttendanceRecord | null>(null);
  const [detailTarget,     setDetailTarget]     = useState<AttendanceRecord | null>(null);
  const [historyTarget,    setHistoryTarget]    = useState<AttendanceRecord | null>(null);
  const [showNewEntry,     setShowNewEntry]     = useState(false);
  const [newEntryPreset,   setNewEntryPreset]   = useState<{ date: string; userId: string; autoActivity?: boolean } | null>(null);
  const [showSpecialEntry, setShowSpecialEntry] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [deleteConfirm,    setDeleteConfirm]    = useState<string | null>(null);
  const [faceModalUrl,     setFaceModalUrl]     = useState<string | null>(null);
  const [activityLogTarget, setActivityLogTarget] = useState<AttendanceRecord | null>(null);
  const [activityBySession, setActivityBySession] = useState<Record<string, ActivityLogEntry[]>>({});
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001/api').replace(/\/api$/, '') + '/api';
  // 統合表示: 通常業務と臨時業務の区分をなくして1つの「業務」として見せる会社かどうか
  const unified = isUnified();
  const [filterVessel,   setFilterVessel]   = useState(() => searchParams.get('vessel') ?? 'all');
  const [filterCrew,     setFilterCrew]     = useState(() => searchParams.get('crew') ?? '');
  const [filterWorkType, setFilterWorkType] = useState(() => searchParams.get('wt') ?? 'all');
  const [crewSuggestOpen, setCrewSuggestOpen] = useState(false);

  const [rangeFrom, setRangeFrom] = useState(() => searchParams.get('from') ?? todayJst());
  const [rangeTo,   setRangeTo]   = useState(() => searchParams.get('to')   ?? todayJst());

  const [tlYear,   setTlYear]   = useState(() => Number(searchParams.get('year'))  || nowJst().getFullYear());
  const [tlMonth,  setTlMonth]  = useState(() => Number(searchParams.get('month')) || (nowJst().getMonth() + 1));
  const [tlUserId, setTlUserId] = useState(() => searchParams.get('uid') ?? '');

  // URL に状態を同期（ページリロードで復元できるように）
  const syncUrl = useCallback(() => {
    const p: Record<string, string> = { view: viewMode };
    if (viewMode === 'timeline') {
      p.year  = String(tlYear);
      p.month = String(tlMonth);
      if (tlUserId) p.uid = tlUserId;
    } else {
      p.from = rangeFrom;
      p.to   = rangeTo;
      if (filterVessel !== 'all') p.vessel = filterVessel;
      if (filterCrew)             p.crew   = filterCrew;
      if (filterWorkType !== 'all') p.wt   = filterWorkType;
    }
    setSearchParams(p, { replace: true });
  }, [viewMode, rangeFrom, rangeTo, tlYear, tlMonth, tlUserId, filterVessel, filterCrew, filterWorkType, setSearchParams]);

  useEffect(() => { syncUrl(); }, [syncUrl]);

  const prevTlMonth = () => {
    if (tlMonth === 1) { setTlYear(y => y - 1); setTlMonth(12); }
    else setTlMonth(m => m - 1);
  };
  const nextTlMonth = () => {
    const n = nowJst();
    if (tlYear > n.getFullYear() || (tlYear === n.getFullYear() && tlMonth >= n.getMonth() + 1)) return;
    if (tlMonth === 12) { setTlYear(y => y + 1); setTlMonth(1); }
    else setTlMonth(m => m + 1);
  };

  // クイック選択
  const applyPreset = (preset: 'today' | 'yesterday' | 'week' | 'month' | 'last_month') => {
    const now = new Date(Date.now() + 9 * 60 * 60 * 1000);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    if (preset === 'today') {
      setRangeFrom(fmt(now)); setRangeTo(fmt(now));
    } else if (preset === 'yesterday') {
      const yest = new Date(now); yest.setDate(yest.getDate() - 1);
      setRangeFrom(fmt(yest)); setRangeTo(fmt(yest));
    } else if (preset === 'week') {
      const from = new Date(now); from.setDate(from.getDate() - 6);
      setRangeFrom(fmt(from)); setRangeTo(fmt(now));
    } else if (preset === 'month') {
      setRangeFrom(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2,'0')}-01`);
      setRangeTo(fmt(now));
    } else if (preset === 'last_month') {
      const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const m = now.getMonth() === 0 ? 12 : now.getMonth();
      const last = new Date(y, m, 0);
      setRangeFrom(`${y}-${String(m).padStart(2,'0')}-01`);
      setRangeTo(fmt(last));
    }
  };

  const shiftPeriod = (dir: -1 | 1) => {
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    const todayStr = fmt(new Date(Date.now() + 9 * 3600000));
    const f = new Date(rangeFrom);
    const t = new Date(rangeTo);
    // Full-month detection: 1st to last day of same month
    if (f.getDate() === 1 && rangeFrom.slice(0, 7) === rangeTo.slice(0, 7)) {
      const lastOfMonth = new Date(f.getFullYear(), f.getMonth() + 1, 0);
      if (t.getDate() === lastOfMonth.getDate()) {
        const newF = new Date(f.getFullYear(), f.getMonth() + dir, 1);
        const newT = new Date(newF.getFullYear(), newF.getMonth() + 1, 0);
        if (fmt(newT) > todayStr) return;
        setRangeFrom(fmt(newF)); setRangeTo(fmt(newT)); return;
      }
    }
    // Shift by same duration
    const days = Math.round((t.getTime() - f.getTime()) / 86400000) + 1;
    const ms = dir * days * 86400000;
    const newF = new Date(f.getTime() + ms);
    const newT = new Date(t.getTime() + ms);
    if (fmt(newT) > todayStr) return;
    setRangeFrom(fmt(newF)); setRangeTo(fmt(newT));
  };

  const periodLabel = rangeFrom === rangeTo ? rangeFrom : `${rangeFrom} 〜 ${rangeTo}`;

  const fetchRecords = async (silent = false) => {
    if (!silent) setLoading(true);
    const [attResult, actResult] = await Promise.allSettled([
      api.get<any>('/attendance', { date_from: rangeFrom, date_to: rangeTo }),
      api.get<any>('/activity-logs', { date_from: rangeFrom, date_to: rangeTo }),
    ]);
    if (attResult.status === 'fulfilled') {
      const attRes = attResult.value;
      const raw = attRes?.records ?? attRes?.data ?? attRes?.attendance ?? [];
      // 臨時業務だけを保持する入れ物セッションは勤怠一覧に出さない（0時間の記録として並ぶのを防ぐ）
      setRecords(raw.filter((r: any) => !r.is_activity_only).map(toRecord));
    } else {
      setRecords([]);
    }
    if (actResult.status === 'fulfilled') {
      const bySession: Record<string, ActivityLogEntry[]> = {};
      for (const log of actResult.value?.data ?? []) {
        if (!bySession[log.work_session_id!]) bySession[log.work_session_id!] = [];
        bySession[log.work_session_id!]!.push(log as ActivityLogEntry);
      }
      setActivityBySession(bySession);
    } else {
      setActivityBySession({});
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get<any>('/users');
      const raw: any[] = res?.data ?? res?.users ?? [];
      const loaded = raw.map(u => ({ id: String(u.id), name: String(u.user_name ?? ''), vessel: String(u.vessel_name ?? ''), vesselId: u.vessel_id ? String(u.vessel_id) : undefined }));
      setUsers(loaded);
      // URLにuidがなければ（または存在しないユーザーなら）先頭ユーザーをセット
      setTlUserId(prev => {
        if (prev && loaded.some(u => u.id === prev)) return prev;
        return loaded[0]?.id ?? '';
      });
    } catch (err) { console.error('[AttendancePage]', err); }
  };

  useEffect(() => {
    fetchRecords();
    const t = setInterval(() => fetchRecords(true), 30000);
    return () => clearInterval(t);
  }, [rangeFrom, rangeTo]);
  useEffect(() => { fetchUsers(); }, []);

  const vessels   = Array.from(new Set(records.map(r => r.vessel).filter(Boolean)));
  const crewNames = Array.from(new Set(records.map(r => r.crewName).filter(Boolean)));
  // 統合表示では臨時業務の作業名も同じ「業務内容」の選択肢にする
  const workTypes = useMemo(() => {
    const set = new Set<string>();
    for (const r of records) if (r.workType) set.add(r.workType);
    if (unified) {
      for (const logs of Object.values(activityBySession)) {
        for (const log of logs) if (log.name) set.add(log.name);
      }
    }
    return Array.from(set);
  }, [records, activityBySession, unified]);

  /** その記録に紐づく臨時業務ログ */
  const logsOf = useCallback(
    (r: AttendanceRecord): ActivityLogEntry[] => activityBySession[r.id] ?? [],
    [activityBySession],
  );

  /**
   * 一覧に出す値。統合表示では作業ログも同じ「業務」として扱うため、
   * 勤務時間外に行った作業を業務時間へ加算し、作業ログだけの記録は作業の時刻を表示する。
   */
  const displayOf = useCallback((r: AttendanceRecord) => {
    const logs = logsOf(r);
    const lb = laborBucketsOf(r, logs);   // 労働区分の内訳（区分の記録が無ければ全て0）
    if (!unified) {
      return { logs, workMin: Math.max(0, r.workMin - lb.nonWorkInsideMin), actMin: 0, clockIn: r.clockIn, clockOut: r.clockOut, exemptMin: lb.exemptMin, safetyOtMin: lb.safetyOtMin, nonWorkMin: lb.nonWorkMin };
    }
    const span = (r.isActivityOnly && logs.length > 0) ? activitySpan(logs) : null;
    return {
      logs,
      workMin:  unifiedWorkMin(r, logs),
      actMin:   activityMin(logs),
      clockIn:  span ? span.start : r.clockIn,
      clockOut: span ? span.end   : r.clockOut,
      exemptMin: lb.exemptMin, safetyOtMin: lb.safetyOtMin, nonWorkMin: lb.nonWorkMin,
    };
  }, [logsOf, unified]);

  // 氏名サジェスト候補（船ごとグループ化 + 部分一致フィルタ）
  const crewSuggestGroups = useMemo(() => {
    const nameToVessel = new Map<string, string>();
    for (const r of records) {
      if (r.crewName && !nameToVessel.has(r.crewName)) nameToVessel.set(r.crewName, r.vessel || '');
    }
    // users にいるが records にない人も含める（期間外の乗組員も選択できるように）
    for (const u of users) {
      if (u.name && !nameToVessel.has(u.name)) nameToVessel.set(u.name, u.vessel || '');
    }
    const query = filterCrew.toLowerCase();
    const byVessel = new Map<string, string[]>();
    for (const [name, vessel] of nameToVessel) {
      if (query && !name.toLowerCase().includes(query)) continue;
      if (!byVessel.has(vessel)) byVessel.set(vessel, []);
      byVessel.get(vessel)!.push(name);
    }
    const groups = [...byVessel.entries()].map(([vessel, names]) => ({
      vessel, names: [...names].sort((a, b) => a.localeCompare(b, 'ja')),
    }));
    groups.sort((a, b) => (!a.vessel && b.vessel) ? 1 : (a.vessel && !b.vessel) ? -1 : a.vessel.localeCompare(b.vessel, 'ja'));
    return groups;
  }, [records, users, filterCrew]);

  const filtered = useMemo(() => records.filter(r => {
    if (filterVessel !== 'all' && r.vessel !== filterVessel) return false;
    if (filterWorkType !== 'all') {
      // 統合表示では臨時業務の作業名でも絞り込めるようにする
      const hitActivity = unified && (activityBySession[r.id] ?? []).some(l => l.name === filterWorkType);
      if (r.workType !== filterWorkType && !hitActivity) return false;
    }
    if (filterCrew && !r.crewName.toLowerCase().includes(filterCrew.toLowerCase())) return false;
    return true;
  }), [records, filterVessel, filterWorkType, filterCrew, activityBySession, unified]);

  // ソート → ページング（テーブル表示用・共通部品）。出力(CSV/Excel)は filtered 全件のまま
  const sort = useSort<AttendanceRecord>(filtered, {
    crewName: r => r.crewName,
    vessel:   r => r.vessel,
    date:     r => r.date,
    clockIn:  r => r.clockInRaw,
    clockOut: r => r.clockOutRaw,
    workMin:  r => displayOf(r).workMin,
    overMin:  r => r.overMin,
    alcohol:  r => r.alcoholValue ?? -1,
    status:   r => r.status,
    workType: r => r.workType ?? '',
  }, 'date');
  const pager = usePager(sort.sorted, { noun: '件' });

  // 業務時間の合計（勤務時間の中で行った作業を二重に数えないロジックはそのまま）
  // 管理者の操作（動作確認・説明用）は勤怠ではないので合計に入れない
  const counted       = useMemo(() => filtered.filter(r => !r.adminOperated), [filtered]);
  const totalWork     = counted.reduce((s, r) => s + displayOf(r).workMin, 0);
  const totalOver     = counted.reduce((s, r) => s + r.overMin, 0);
  const working       = counted.filter(r => r.status === 'working').length;

  /**
   * 統合表示の内訳: 「何をどれだけ作業したか」を作業名ごとの 合計時間 と 回数 で出す。
   * 回数は 出退勤の記録1件＝1回 ／ 時間を指定した作業ログ1件＝1回。同じ作業名なら合算する。
   */
  const workBreakdown = useMemo(() => {
    if (!unified) return [] as { name: string; min: number; count: number; emoji: string }[];
    const map: Record<string, { name: string; min: number; count: number; emoji: string }> = {};
    const put = (name: string, min: number, emoji: string) => {
      if (!map[name]) map[name] = { name, min: 0, count: 0, emoji };
      map[name]!.min   += min;
      map[name]!.count += 1;
      if (!map[name]!.emoji) map[name]!.emoji = emoji;
    };
    for (const r of counted) {
      const logs = logsOf(r);
      // 作業ログだけの入れ物セッションは1回として数えない（実体は作業ログ側）
      if (!(r.isActivityOnly && logs.length > 0)) put(r.workType || '未設定', r.workMin, '');
      for (const log of logs) put(log.name, log.duration_minutes ?? 0, log.emoji ?? '');
    }
    return Object.values(map).sort((a, b) => b.min - a.min);
  }, [counted, logsOf, unified]);

  // 出力の列構成と行データ。統合表示では業務内容ごとの1行にまとめる（1行＝1回として数えられる）
  const exportColumns = unified ? UNIFIED_EXPORT_COLUMNS : EXPORT_COLUMNS;
  const exportRows = () => (unified ? unifiedRows(counted, activityBySession) : counted.map(recordToRow));

  const handleActivityForDay = async (date: string, userId: string) => {
    const year = parseInt(date.slice(0, 4));
    const month = parseInt(date.slice(5, 7));
    try {
      const res = await api.get<{ data: any[] }>('/attendance', { user_id: userId, year: String(year), month: String(month) });
      const daySession = (res.data ?? []).find((r: any) => (r.work_date ?? r.target_date ?? '').slice(0, 10) === date);
      if (daySession) {
        setActivityLogTarget({
          id: String(daySession.id),
          crewName: users.find(u => u.id === userId)?.name ?? String(daySession.user_name ?? ''),
          vessel: String(daySession.vessel_name ?? ''),
          vessel_id: String(daySession.vessel_id ?? ''),
          date,
          clockIn: '', clockOut: '',
          clockInRaw: String(daySession.clock_in ?? ''), clockOutRaw: String(daySession.clock_out ?? ''),
          breakMin: Number(daySession.break_minutes ?? 60),
          workMin: Number(daySession.work_minutes ?? 0),
          overMin: Number(daySession.over_minutes ?? 0),
          status: (daySession.status ?? 'completed') as AttendanceRecord['status'],
          userId: String(daySession.user_id ?? userId ?? ''),
          recordSource: 'gps_auto', editHistory: [],
        });
      } else {
        await createActivityOnlySession(date, userId);
      }
    } catch {
      await createActivityOnlySession(date, userId);
    }
  };

  // その日に通常勤務のセッションが無い場合、特別作業だけの入れ物セッションを自動作成し
  // 特別作業モーダルを直接開く（通常業務の手動追加フォームは出さない）。
  const createActivityOnlySession = async (date: string, userId: string) => {
    try {
      const res = await api.post<{ data: { id: string; vessel_id: string; work_date: string } }>('/attendance', {
        user_id: userId, work_date: date, activity_only: true,
        reason: unified ? '作業の記録' : '特別作業の記録',
      });
      const u = users.find(x => x.id === userId);
      setActivityLogTarget({
        id: String(res.data.id), userId, crewName: u?.name ?? '', vessel: u?.vessel ?? '',
        vessel_id: String(res.data.vessel_id ?? ''), date,
        clockIn: '', clockOut: '', clockInRaw: '', clockOutRaw: '',
        breakMin: 0, workMin: 0, overMin: 0, status: 'completed',
        recordSource: 'admin_input', editHistory: [],
      });
    } catch (e: any) {
      alert(e?.message ?? (unified ? '作業の記録作成に失敗しました' : '特別作業の記録作成に失敗しました'));
    }
  };

  const handleSave = async (updated: AttendanceRecord, history: EditHistory, clockOutDate?: string, clockInDate?: string) => {
    try {
      await api.put('/attendance/' + updated.id, {
        clock_in:       updated.clockIn,
        clock_out:      updated.clockOut,
        clock_in_date:  clockInDate,
        clock_out_date: clockOutDate,
        work_type:      updated.workType ?? null,
        reason:         history.reason,
      });
      setRecords(prev => prev.map(r => r.id === updated.id ? { ...updated, editHistory: [...r.editHistory, history] } : r));
      setTimelineRefreshKey(k => k + 1);
      setEditTarget(null);
      fetchRecords();
    } catch (err) { console.error('[AttendancePage] sync:', err); }
  };

  const handleTimelineEdit = (rec: TimelineRecord) => {
    setEditTarget({
      id:            rec.id,
      crewName:      rec.crewName,
      vessel:        rec.vessel,
      vessel_id:     (rec as any).vessel_id ?? '',
      date:          rec.date,
      clockIn:       fmtLocal(rec.clockInRaw),
      clockOut:      fmtLocal(rec.clockOutRaw),
      clockInRaw:    rec.clockInRaw,
      clockOutRaw:   rec.clockOutRaw,
      breakMin:      rec.breakMin,
      workMin:       rec.workMin,
      overMin:       rec.overMin,
      workType:      rec.workType,
      status:        rec.status as AttendanceRecord['status'],
      userId:        (rec as any).userId ?? '',
      recordSource:  'gps_auto',
      editHistory:   [],
      note:          '',
      alcoholValue:  undefined,
      faceImagePath: undefined,
      authMethod:    undefined,
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await api.del('/attendance/' + id);
      setRecords(prev => prev.filter(r => r.id !== id));
      setDeleteConfirm(null);
      setTimelineRefreshKey(k => k + 1);
    } catch (err) { console.error('[AttendancePage] sync:', err); }
  };

  const SOURCE_DOT: Record<RecordSource, { color: string; label: string }> = {
    gps_auto:    { color: '#0ea5e9', label: '自動' },
    admin_input: { color: '#f59e0b', label: '管理者' },
    crew_edit:   { color: '#8b5cf6', label: '乗組員' },
  };

  const STATUS_BADGE: Record<string, { color: string; bg: string; label: string }> = {
    completed: { color: '#14532d', bg: '#86efac', label: '完了' },
    working:   { color: '#1e3a8a', bg: '#93c5fd', label: '勤務中' },
    absent:    { color: '#7f1d1d', bg: '#fca5a5', label: '欠勤' },
    edited:    { color: '#78350f', bg: '#fcd34d', label: '修正済' },
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif' }}>

      {/* ページヘッダー */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '20px 28px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0f172a' }}>勤怠管理</h1>
            <p style={{ margin: '3px 0 0', color: '#64748b', fontSize: 13 }}>{periodLabel} の勤怠記録</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <MasterLinkButton tab="work" title="業務種別・勤怠閾値などのマスタを開きます" />
            {/* 表示切り替え */}
            <div style={{ display: 'flex', background: '#F1F5F9', borderRadius: 10, padding: 3, gap: 2 }}>
              <button onClick={() => setViewMode('table')} style={{
                display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                background: viewMode === 'table' ? '#fff' : 'transparent',
                color: viewMode === 'table' ? '#0f172a' : '#64748b',
                boxShadow: viewMode === 'table' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
              }}>
                <LayoutList size={14} /> テーブル
              </button>
              <button onClick={() => setViewMode('timeline')} style={{
                display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                background: viewMode === 'timeline' ? '#fff' : 'transparent',
                color: viewMode === 'timeline' ? '#0f172a' : '#64748b',
                boxShadow: viewMode === 'timeline' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
              }}>
                <BarChart2 size={14} /> タイムライン
              </button>
            </div>
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowDownloadMenu(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: '#fff', color: '#0f172a', border: '1.5px solid #e2e8f0', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                <Download size={14} /> ダウンロード ▾
              </button>
              {showDownloadMenu && (
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 200, minWidth: 200, overflow: 'hidden' }}
                  onMouseLeave={() => setShowDownloadMenu(false)}>
                  <button
                    onClick={() => { downloadCsv(exportColumns, exportRows(), `勤怠_${periodLabel}.csv`); setShowDownloadMenu(false); }}
                    style={{ width: '100%', padding: '11px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0f172a', textAlign: 'left' }}>
                    CSVダウンロード (.csv)
                  </button>
                  <button
                    onClick={() => { downloadExcel(exportColumns, exportRows(), `勤怠_${periodLabel}.xlsx`, periodLabel, unified ? UNIFIED_EXPORT_NOTE : undefined).catch(console.error); setShowDownloadMenu(false); }}
                    style={{ width: '100%', padding: '11px 16px', border: 'none', borderTop: '1px solid #f1f5f9', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0f172a', textAlign: 'left' }}>
                    Excelダウンロード (.xlsx)
                  </button>
                  {unified && (
                    <div style={{ padding: '8px 16px 10px', borderTop: '1px solid #f1f5f9', fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>
                      業務内容ごとに1行で出力します（「業務内容」で絞り込めば時間と回数を集計できます）
                    </div>
                  )}
                </div>
              )}
            </div>
            <button onClick={() => setShowSpecialEntry(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: actAccent().bg, color: actAccent().fg, border: `1.5px solid ${actAccent().border}`, borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
              ＋ {actLabel.add()}
            </button>
            <button onClick={() => setShowNewEntry(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
              ＋ 手動追加
            </button>
          </div>
        </div>
        {hasFeature('alcohol_linked_attendance') && <AlcoholApprovalPanel onApplied={() => { fetchRecords(true); }} />}

        {/* タイムラインビュー */}
        {viewMode === 'timeline' && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid #e2e8f0' }}>
              <button onClick={prevTlMonth}
                style={{ padding: '6px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#475569' }}>
                ← 前月
              </button>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', minWidth: 110, textAlign: 'center' }}>
                {tlYear}年{tlMonth}月
              </span>
              <button onClick={nextTlMonth}
                disabled={(() => { const n = nowJst(); return tlYear > n.getFullYear() || (tlYear === n.getFullYear() && tlMonth >= n.getMonth() + 1); })()}
                style={{ padding: '6px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#475569', opacity: (() => { const n = nowJst(); return tlYear > n.getFullYear() || (tlYear === n.getFullYear() && tlMonth >= n.getMonth() + 1) ? 0.35 : 1; })() }}>
                翌月 →
              </button>
            </div>
            <AttendanceTimeline
              users={users}
              year={tlYear}
              month={tlMonth}
              selectedUserId={tlUserId || undefined}
              onUserChange={setTlUserId}
              onEdit={handleTimelineEdit}
              onNewEntry={(date, userId) => setNewEntryPreset({ date, userId })}
              onActivityForDay={(date, userId) => handleActivityForDay(date, userId)}
              onActivity={(rec) => setActivityLogTarget({
                id: rec.id,
                crewName: rec.crewName,
                vessel: rec.vessel,
                vessel_id: (rec as any).vessel_id ?? '',
                date: rec.date,
                clockIn: '',
                clockOut: '',
                clockInRaw: rec.clockInRaw,
                clockOutRaw: rec.clockOutRaw,
                breakMin: rec.breakMin,
                workMin: rec.workMin,
                overMin: rec.overMin,
                workType: rec.workType,
                status: rec.status as any,
                userId: (rec as any).userId ?? '',
                recordSource: 'gps_auto',
                editHistory: [],
                note: '',
              })}
              onDelete={(rec) => setDeleteConfirm(rec.id)}
              refreshKey={timelineRefreshKey}
            />
          </div>
        )}

      </div>

      <div style={{ padding: '20px 28px' }}>

        {/* フィルターバー（テーブルモードのみ） */}
        {viewMode === 'table' && <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 18px', marginBottom: 16 }}>

          {/* 1行目: MoneyForward ナビ + クイック選択 + カスタム日付 */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
            {/* ← › ナビゲーター */}
            <button onClick={() => shiftPeriod(-1)} style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '5px 11px', fontSize: 17, lineHeight: 1, cursor: 'pointer', color: '#475569', fontWeight: 700 }}>‹</button>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', minWidth: 230, textAlign: 'center', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '6px 14px', userSelect: 'none' }}>
              {rangeFrom === rangeTo ? rangeFrom.replace(/-/g, '/') : `${rangeFrom.replace(/-/g, '/')} 〜 ${rangeTo.replace(/-/g, '/')}`}
            </span>
            <button onClick={() => shiftPeriod(1)} style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '5px 11px', fontSize: 17, lineHeight: 1, cursor: 'pointer', color: '#475569', fontWeight: 700 }}>›</button>

            <div style={{ width: 1, height: 22, background: '#e2e8f0', margin: '0 2px' }} />

            {([
              { key: 'today',      label: '今日' },
              { key: 'yesterday',  label: '昨日' },
              { key: 'week',       label: '今週' },
              { key: 'month',      label: '今月' },
              { key: 'last_month', label: '先月' },
            ] as const).map(p => (
              <button
                key={p.key}
                onClick={() => applyPreset(p.key)}
                style={{ padding: '7px 14px', fontSize: 13, fontWeight: 600, borderRadius: 6, border: '1.5px solid #e2e8f0', cursor: 'pointer', background: '#f8fafc', color: '#374151', transition: 'all 0.12s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#0066CC'; e.currentTarget.style.color = '#0066CC'; e.currentTarget.style.background = '#EBF4FF'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#374151'; e.currentTarget.style.background = '#f8fafc'; }}
              >{p.label}</button>
            ))}
            <div style={{ width: 1, height: 22, background: '#e2e8f0', margin: '0 4px' }} />
            <input type="date" value={rangeFrom} onChange={e => setRangeFrom(e.target.value)}
              style={{ border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '7px 10px', fontSize: 14, color: '#0f172a', outline: 'none' }} />
            <span style={{ color: '#94a3b8', fontSize: 14 }}>〜</span>
            <input type="date" value={rangeTo} onChange={e => setRangeTo(e.target.value)}
              style={{ border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '7px 10px', fontSize: 14, color: '#0f172a', outline: 'none' }} />
          </div>

          {/* 2行目: 絞り込み + サマリー */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#374151' }}>
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>船舶</span>
              <select value={filterVessel} onChange={e => setFilterVessel(e.target.value)}
                style={{ border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '7px 10px', fontSize: 14, outline: 'none', background: '#fff' }}>
                <option value="all">すべて</option>
                {vessels.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#374151' }}>
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{unified ? '業務内容' : '作業種別'}</span>
              <select value={filterWorkType} onChange={e => setFilterWorkType(e.target.value)}
                style={{ border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '7px 10px', fontSize: 14, outline: 'none', background: '#fff' }}>
                <option value="all">すべて</option>
                {workTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#374151' }}>
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>氏名</span>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={filterCrew}
                  onChange={e => setFilterCrew(e.target.value)}
                  onFocus={() => setCrewSuggestOpen(true)}
                  onBlur={() => setTimeout(() => setCrewSuggestOpen(false), 150)}
                  placeholder="氏名で検索..."
                  style={{ border: `1.5px solid ${crewSuggestOpen ? '#93c5fd' : '#e2e8f0'}`, borderRadius: 7, padding: '7px 32px 7px 10px', fontSize: 14, outline: 'none', background: '#fff', width: 160 }}
                />
                {filterCrew && (
                  <button
                    onMouseDown={() => setFilterCrew('')}
                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, padding: 0, lineHeight: 1 }}
                  >✕</button>
                )}
                {crewSuggestOpen && crewSuggestGroups.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, marginTop: 4,
                    background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 300,
                    minWidth: 200, maxHeight: 280, overflowY: 'auto',
                  }}>
                    {crewSuggestGroups.map(({ vessel, names }) => (
                      <div key={vessel || '__none__'}>
                        <div style={{ padding: '6px 14px 4px', fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', position: 'sticky', top: 0 }}>
                          {vessel ? `🚢 ${vessel}` : '（未配属）'}
                        </div>
                        {names.map(name => (
                          <div
                            key={name}
                            onMouseDown={() => { setFilterCrew(name); setCrewSuggestOpen(false); }}
                            style={{ padding: '9px 14px 9px 20px', fontSize: 13, color: '#0f172a', cursor: 'pointer', borderBottom: '1px solid #f8fafc' }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                          >
                            {name}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </label>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 16, alignItems: 'center', paddingLeft: 14, borderLeft: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: 13, color: '#475569', whiteSpace: 'nowrap' }}><b style={{ color: '#0f172a', fontSize: 15 }}>{filtered.length}</b> 件</span>
              <span style={{ fontSize: 13, color: '#475569', whiteSpace: 'nowrap' }}>
                {unified ? '業務' : '勤務'} <b style={{ color: '#0f172a' }}>{fmtMin(totalWork)}</b>
              </span>
              <span style={{ fontSize: 13, color: '#475569', whiteSpace: 'nowrap' }}>残業 <b style={{ color: totalOver > 0 ? '#dc2626' : '#475569' }}>{fmtMin(totalOver)}</b></span>
              {working > 0 && <span style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8', whiteSpace: 'nowrap' }}>{working}名 勤務中</span>}
            </div>
          </div>

          {/* 統合表示: 何をどれだけ作業したか（作業名ごとの合計時間と回数） */}
          {unified && workBreakdown.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap' }}>業務内容ごとの時間と回数</span>
              {workBreakdown.map(w => (
                <span key={w.name} style={{ ...WORK_BADGE_STYLE(), borderRadius: 20, padding: '3px 10px', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {w.emoji ? `${w.emoji} ` : ''}{w.name} {fmtDur(w.min)}（{w.count}回）
                </span>
              ))}
            </div>
          )}
        </div>}

        {/* テーブル（テーブルモードのみ） */}
        {viewMode === 'table' && !loading && pager.renderBar('top')}
        {viewMode === 'table' && <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', fontSize: 14 }}>読み込み中...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 900 }}>
              <thead>
                <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                  <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#374151', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>顔写真</th>
                  <SortableTh label="乗組員" k="crewName" sort={sort} />
                  <SortableTh label="船舶" k="vessel" sort={sort} />
                  <SortableTh label="日付" k="date" sort={sort} />
                  <SortableTh label="業務開始" k="clockIn" sort={sort} />
                  <SortableTh label="業務終了" k="clockOut" sort={sort} />
                  <SortableTh label={unified ? '業務時間' : '勤務'} k="workMin" sort={sort} />
                  <SortableTh label="残業" k="overMin" sort={sort} />
                  {hasFeature('labor_categories') && <th style={{ whiteSpace: 'nowrap' }} title="非常作業（68条）／安全臨時労働（64条）">別枠</th>}
                  {hasFeature('labor_categories') && <th style={{ whiteSpace: 'nowrap' }} title="労働時間に含めない記録（食事など）">労働外</th>}
                  <SortableTh label="アルコール" k="alcohol" sort={sort} />
                  <SortableTh label="状態" k="status" sort={sort} />
                  <SortableTh label={unified ? '業務内容' : '作業種別'} k="workType" sort={sort} />
                  <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#374151', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {pager.pageRows.length === 0 ? (
                  <tr><td colSpan={12} style={{ textAlign: 'center', padding: 48, color: '#94a3b8', fontSize: 14 }}>該当する勤怠記録がありません</td></tr>
                ) : pager.pageRows.map((r, i) => {
                  const st = STATUS_BADGE[r.status] ?? { color: '#166534', bg: '#dcfce7', label: '完了' };
                  const sd = SOURCE_DOT[r.recordSource];
                  // 統合表示では臨時業務も同じ「業務」として時間・時刻に反映する
                  const d = displayOf(r);
                  const alco = r.alcoholValue;
                  const alcoJudge = alco == null ? null : judgeAlcohol(alco, alcoTh);
                  const alcoInfo = alcoJudge == null ? null
                    : alcoJudge === 'normal'  ? { text: alco!.toFixed(3), label: '正常',   color: '#166534', bg: '#dcfce7', dotColor: '#22c55e' }
                    : alcoJudge === 'warning' ? { text: alco!.toFixed(3), label: '要確認', color: '#92400e', bg: '#fef3c7', dotColor: '#eab308' }
                    :                           { text: alco!.toFixed(3), label: '超過',   color: '#991b1b', bg: '#fee2e2', dotColor: '#ef4444' };
                  return (
                    <tr key={r.id}
                      onClick={() => {
                        setDetailTarget(r);
                        const u = users.find(u => u.name === r.crewName) ?? users.find(u => u.id === r.userId);
                        if (u) setTlUserId(u.id);
                      }}
                      style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #e2e8f0', transition: 'background 0.1s', cursor: 'pointer' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')}
                      onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#f8fafc')}
                    >
                      <td style={{ padding: '8px 14px' }}>
                        {r.faceImagePath ? (
                          <FaceImage apiBase={API_BASE} path={r.faceImagePath} onOpen={setFaceModalUrl} />
                        ) : (
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f1f5f9', border: '2px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}><User size={15} /></div>
                        )}
                      </td>
                      <td style={{ padding: '13px 14px', fontWeight: 700, color: '#0f172a', fontSize: 14 }}>
                        {r.crewName}
                        {r.adminOperated && (
                          <span title="管理者の操作（船員デモ・配乗の無い管理者の打刻）です。動作確認・説明用の記録なので、合計・出力・休息時間の判定には含まれません"
                            style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#4338ca', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap' }}>
                            管理者操作
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '13px 14px', color: '#374151', fontSize: 13 }}>{r.vessel || '-'}</td>
                      <td style={{ padding: '13px 14px', color: '#374151', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{r.date}</td>
                      <td style={{ padding: '13px 14px', fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{d.clockIn || '-'}</td>
                      <td style={{ padding: '13px 14px', fontWeight: 700, color: r.status === 'working' ? '#1d4ed8' : '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                        {d.clockOut || <span style={{ color: '#1d4ed8', fontSize: 11, fontWeight: 600 }}>● 勤務中</span>}
                      </td>
                      <td style={{ padding: '13px 14px', color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                        {fmtMin(d.workMin)}
                      </td>
                      <td style={{ padding: '13px 14px', fontVariantNumeric: 'tabular-nums' }}>
                        {r.overMin > 0
                          ? <span style={{ color: '#dc2626', fontWeight: 700 }}>+{fmtMin(r.overMin)}</span>
                          : <span style={{ color: '#94a3b8' }}>-</span>}
                      </td>
                      {hasFeature('labor_categories') && (
                        <td style={{ padding: '13px 14px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                          title="非常作業（68条・上限計算から除外）／安全臨時労働（64条・上限超えが合法）">
                          {(d.exemptMin > 0 || d.safetyOtMin > 0)
                            ? <span style={{ color: '#b91c1c', fontWeight: 700, fontSize: 12 }}>
                                {d.exemptMin > 0 && <span>非常 {fmtDur(d.exemptMin)}</span>}
                                {d.exemptMin > 0 && d.safetyOtMin > 0 && ' / '}
                                {d.safetyOtMin > 0 && <span>安全臨時 {fmtDur(d.safetyOtMin)}</span>}
                              </span>
                            : <span style={{ color: '#94a3b8' }}>-</span>}
                        </td>
                      )}
                      {hasFeature('labor_categories') && (
                        <td style={{ padding: '13px 14px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }} title="食事・自発的作業・私的作業など、労働時間に含めない記録">
                          {d.nonWorkMin > 0
                            ? <span style={{ color: '#64748b', fontWeight: 700, fontSize: 12 }}>{fmtDur(d.nonWorkMin)}</span>
                            : <span style={{ color: '#94a3b8' }}>-</span>}
                        </td>
                      )}
                      <td style={{ padding: '13px 14px' }}>
                        {alcoInfo
                          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: alcoInfo.bg, color: alcoInfo.color, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: alcoInfo.dotColor, display: 'inline-block', flexShrink: 0 }} /> {alcoInfo.text} mg/L
                            </span>
                          : <span style={{ color: '#cbd5e1', fontSize: 12 }}>-</span>}
                      </td>
                      <td style={{ padding: '13px 14px' }}>
                        <span style={{ background: st.bg, color: st.color, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{st.label}</span>
                      </td>
                      <td style={{ padding: '13px 14px' }}>
                        {unified ? (
                          /* 統合表示: 出退勤の作業名も時間指定の作業も同じ体裁のバッジで横並びに見せる（区分を出さない） */
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                            {r.workType && (
                              <span style={{ ...WORK_BADGE_STYLE(), borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{r.workType}</span>
                            )}
                            {d.logs.map(log => (
                              <span key={log.id} style={{ ...WORK_BADGE_STYLE(), display: 'inline-flex', alignItems: 'center', gap: 3, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                {log.emoji} {log.name} {log.duration_minutes}分
                              </span>
                            ))}
                            {!r.workType && d.logs.length === 0 && <span style={{ color: '#CBD5E1', fontSize: 12 }}>-</span>}
                          </div>
                        ) : (
                          <>
                            {r.workType
                              ? <span style={{ background: '#F3E8FF', color: '#7C3AED', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{r.workType}</span>
                              : <span style={{ color: '#CBD5E1', fontSize: 12 }}>-</span>}
                            {d.logs.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                                {d.logs.map(log => (
                                  <span key={log.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, padding: '2px 8px', borderRadius: 99, background: log.color + '22', color: log.color, border: `1px solid ${log.color}44`, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                    {log.emoji} {log.name} {log.duration_minutes}分
                                  </span>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td style={{ padding: '13px 14px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => setEditTarget(r)}
                            style={{ padding: '4px 10px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>編集</button>
                          <button onClick={() => setHistoryTarget(r)}
                            style={{ padding: '4px 10px', background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>履歴</button>
                          <button
                            onClick={() => setActivityLogTarget(r)}
                            title={unified ? '時間を指定して作業を記録します' : '特別作業を記録します'}
                            style={{ padding: '6px 12px', fontSize: 12, border: `1px solid ${actAccent().border}`, borderRadius: 6, background: actAccent().bg, color: actAccent().fg, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
                          >
                            {actLabel.rowBtn()}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const u = users.find(u => u.name === r.crewName) ?? users.find(u => u.id === r.userId);
                              if (u) setTlUserId(u.id);
                              setViewMode('timeline');
                            }}
                            style={{ padding: '4px 10px', background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>TL</button>
                          <button onClick={() => setDeleteConfirm(r.id)}
                            style={{ padding: '4px 10px', background: '#fff5f5', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>削除</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>}
        {viewMode === 'table' && !loading && pager.renderBar('bottom')}

      </div>

      {/* 顔写真モーダル */}
      {faceModalUrl && (
        <div onClick={() => setFaceModalUrl(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.4)', maxWidth: 480, width: '100%' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', display: 'inline-flex', alignItems: 'center', gap: 6 }}><Camera size={15} /> 本人確認写真</span>
              <button onClick={() => setFaceModalUrl(null)} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>✕</button>
            </div>
            <img src={faceModalUrl} alt="顔写真" style={{ width: '100%', display: 'block', maxHeight: 480, objectFit: 'contain', background: '#000' }} />
            <div style={{ padding: '12px 20px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
              クリックで閉じる
            </div>
          </div>
        </div>
      )}

      {showSpecialEntry && (
        <SpecialActivityPickerModal
          users={users}
          onSubmit={(userId, date) => handleActivityForDay(date, userId)}
          onClose={() => setShowSpecialEntry(false)}
        />
      )}
      {detailTarget     && <DetailModal record={detailTarget} apiBase={API_BASE} onOpenPhoto={setFaceModalUrl}
                              onClose={() => setDetailTarget(null)}
                              onEdit={() => { const r = detailTarget; setDetailTarget(null); setEditTarget(r); }} />}
      {editTarget       && <EditModal record={editTarget} onSave={handleSave} onClose={() => setEditTarget(null)} />}
      {historyTarget    && <HistoryPanel record={historyTarget} onClose={() => setHistoryTarget(null)} />}
      {(showNewEntry || newEntryPreset) && (
        <NewEntryModal
          users={users}
          defaultDate={newEntryPreset?.date}
          defaultUserId={newEntryPreset?.userId}
          autoActivity={newEntryPreset?.autoActivity}
          onSave={() => { setShowNewEntry(false); setNewEntryPreset(null); fetchRecords(); setTimelineRefreshKey(k => k + 1); }}
          onClose={() => { setShowNewEntry(false); setNewEntryPreset(null); }}
          onSavedWithActivity={(sessionId, vesselId, workDate) => {
            const targetUserId = newEntryPreset?.userId ?? users[0]?.id ?? '';
            setActivityLogTarget({
              id: sessionId, crewName: users.find(u => u.id === targetUserId)?.name ?? '',
              vessel: '', vessel_id: vesselId, date: workDate,
              clockIn: '', clockOut: '', clockInRaw: '', clockOutRaw: '',
              breakMin: 60, workMin: 0, overMin: 0, status: 'completed',
              userId: targetUserId, recordSource: 'admin_input', editHistory: [],
            });
          }}
        />
      )}
      {activityLogTarget && (
        <ActivityLogModal
          record={activityLogTarget}
          onClose={() => { setActivityLogTarget(null); fetchRecords(); }}
        />
      )}

      {deleteConfirm && (() => {
        const target = records.find(r => r.id === deleteConfirm);
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 380, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>勤怠レコードを削除</div>
              {target && (
                <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', margin: '12px 0', fontSize: 13 }}>
                  <strong>{target.crewName}</strong>　{target.vessel}　{target.date}<br />
                  <span style={{ color: '#475569' }}>{target.clockIn || '-'} → {target.clockOut || '勤務中'}</span>
                </div>
              )}
              <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b' }}>この操作は取り消せません。本当に削除しますか？</p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>キャンセル</button>
                <button onClick={() => handleDelete(deleteConfirm)} style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 10, background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>削除する</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
